import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CookieService } from './cookie.service';
import { STORAGE_KEYS } from '../models/auth.models';

// Tipo derivado dos valores de STORAGE_KEYS — atualiza automaticamente se novas chaves forem adicionadas
type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];

@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly cookieService = inject(CookieService);

  // Chaves que vivem EXCLUSIVAMENTE em cookie — nunca no localStorage (exceto em localhost)
  private readonly cookieOnlyKeys = new Set<StorageKey>([
    STORAGE_KEYS.ACCESS_TOKEN,
    STORAGE_KEYS.REFRESH_TOKEN,
  ]);

  private isCookieOnly(key: string): key is StorageKey {
    return this.cookieOnlyKeys.has(key as StorageKey);
  }

  set<T>(key: string, value: T, shared = false): void {
    if (!this.isBrowser || !key) return;

    const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);

    if (shared || this.isCookieOnly(key)) {
      this.cookieService.set(key, serializedValue);
    } else {
      localStorage.setItem(key, serializedValue);
    }
  }

  get<T>(key: string): T | null {
    if (!this.isBrowser || !key) return null;

    // 1. Tenta cookie (prioridade para tokens)
    let value = this.cookieService.get(key);

    if (value !== null) {
      return this.parseValue<T>(value);
    }

    // 2. Fallback para localStorage
    try {
      const item = localStorage.getItem(key);
      return item !== null ? this.parseValue<T>(item) : null;
    } catch {
      return null;
    }
  }

  remove(key: string): void {
    if (!this.isBrowser || !key) return;

    if (this.isCookieOnly(key)) {
      this.cookieService.remove(key);
    }

    localStorage.removeItem(key);
  }

  clear(): void {
    if (!this.isBrowser) return;
    localStorage.clear();
  }

  private parseValue<T>(value: string): T {
    try {
      return JSON.parse(value) as T;
    } catch {
      return value as unknown as T;
    }
  }
}